/**
 * Mobile Navigation Keyboard Accessibility
 *
 * Handles:
 * - Keyboard navigation for mobile menu bar buttons (search, cart, hamburger)
 * - Focus trap when mobile menu drawer is open
 * - Escape key to close menu
 * - ARIA state management
 */
(() => {
	'use strict';

	/** @type {HTMLElement|null} */
	let mobileMenuDrawer = null;

	/** @type {HTMLElement|null} */
	let mobileMenuToggle = null;

	/** @type {HTMLElement[]} */
	let focusableElements = [];

	/** @type {HTMLElement|null} */
	let lastFocusedBeforeOpen = null;

	/**
	 * Get all currently visible focusable elements within the mobile menu drawer.
	 * This includes the close button, top-level menu items, and any expanded submenus.
	 * @returns {HTMLElement[]}
	 */
	const getFocusableElements = () => {
		if (!mobileMenuDrawer) return [];

		const focusable = [];

		// First, add the hamburger/close button so it's at the top of tab order
		if (mobileMenuToggle) {
			focusable.push(mobileMenuToggle);
		}

		// Get all top-level menu items (direct children of #mobile-menu)
		const topLevelItems = mobileMenuDrawer.querySelectorAll(':scope > li');

		topLevelItems.forEach((item) => {
			// Add links in top-level items
			const link = item.querySelector('.narrow-container > a.menu-link');
			if (link) focusable.push(link);

			// Add submenu toggle buttons
			const button = item.querySelector('.narrow-container > button.submenu-button');
			if (button) focusable.push(button);

			// If submenu is expanded (has 'toggle' class), add its links
			const submenu = item.querySelector(':scope > .submenu-dropdown');
			if (submenu && submenu.classList.contains('toggle')) {
				const submenuLinks = submenu.querySelectorAll('a[href], button');
				submenuLinks.forEach((link) => focusable.push(link));
			}
		});

		// Add language switcher links if present
		const langContainer = mobileMenuDrawer.querySelector('.language-container');
		if (langContainer) {
			const langLinks = langContainer.querySelectorAll('a[href]');
			langLinks.forEach((link) => focusable.push(link));
		}

		return focusable;
	};

	/**
	 * Check if mobile menu is currently open.
	 * @returns {boolean}
	 */
	const isMenuOpen = () => {
		return mobileMenuDrawer && mobileMenuDrawer.classList.contains('toggle');
	};

	/**
	 * Trap focus within the mobile menu drawer.
	 * @param {KeyboardEvent} e
	 */
	const trapFocus = (e) => {
		if (!isMenuOpen()) return;
		if (e.key !== 'Tab') return;

		// Refresh the list of focusable elements on every Tab press
		focusableElements = getFocusableElements();

		// If no focusable elements, prevent tab completely
		if (focusableElements.length === 0) {
			e.preventDefault();
			return;
		}

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];
		const activeElement = document.activeElement;

		// Check if active element is in our focusable list
		const isInFocusableList = focusableElements.includes(activeElement);

		// If focus escaped to an element not in our list, bring it back
		if (!isInFocusableList) {
			e.preventDefault();
			firstElement.focus();
			return;
		}

		// Get the current index
		const currentIndex = focusableElements.indexOf(activeElement);

		// If Shift+Tab on first element, go to last
		if (e.shiftKey && (currentIndex === 0 || currentIndex === -1)) {
			e.preventDefault();
			lastElement.focus();
		}
		// If Tab on last element, go to first
		else if (
			!e.shiftKey &&
			(currentIndex === focusableElements.length - 1 || currentIndex === -1)
		) {
			e.preventDefault();
			firstElement.focus();
		}
		// Otherwise, natural tab order handles moving between elements
		// Don't prevent default in the middle
	};

	/**
	 * Handle Escape key to close menu.
	 * @param {KeyboardEvent} e
	 */
	const handleEscape = (e) => {
		if (e.key === 'Escape' || e.key === 'Esc') {
			if (isMenuOpen() && mobileMenuToggle) {
				// Trigger click on toggle to close via existing data-jtoggle handler
				mobileMenuToggle.click();
				// Return focus to toggle button
				mobileMenuToggle.focus();
			}
		}
	};

	/**
	 * Handle arrow key navigation within the mobile menu.
	 * @param {KeyboardEvent} e
	 */
	const handleArrowKeys = (e) => {
		if (!isMenuOpen()) return;

		const key = e.key;
		if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Up' && key !== 'Down') return;

		// Get current focusable elements
		const focusable = getFocusableElements();
		if (focusable.length === 0) return;

		const activeElement = document.activeElement;
		const currentIndex = focusable.indexOf(activeElement);

		// Only handle arrow keys if we're focused on a menu element
		if (currentIndex === -1) return;

		e.preventDefault();

		if (key === 'ArrowDown' || key === 'Down') {
			// Move to next item, or wrap to first
			const nextIndex = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
			focusable[nextIndex].focus();
		} else if (key === 'ArrowUp' || key === 'Up') {
			// Move to previous item, or wrap to last
			const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
			focusable[prevIndex].focus();
		}
	};

	/**
	 * Hide background content from screen readers and keyboard navigation.
	 */
	const hideBackgroundContent = () => {
		const mainContent = document.querySelector('main, #main, [role="main"]');
		const footer = document.querySelector('footer, [role="contentinfo"]');
		const navbar = document.getElementById('mobile-navbar');

		// Set aria-hidden and inert on main content areas
		if (mainContent) {
			mainContent.setAttribute('aria-hidden', 'true');
			if ('inert' in HTMLElement.prototype) {
				mainContent.setAttribute('inert', '');
			}
		}
		if (footer) {
			footer.setAttribute('aria-hidden', 'true');
			if ('inert' in HTMLElement.prototype) {
				footer.setAttribute('inert', '');
			}
		}

		// Hide the menu bar buttons from keyboard EXCEPT the hamburger (close) button
		if (navbar) {
			const menuBar = navbar.querySelector('.main-bar');
			if (menuBar) {
				// Set tabindex="-1" on search and cart, but NOT on the menu toggle button
				menuBar.querySelectorAll('button, a[href], a').forEach((el) => {
					// Keep the mobile-menu-toggle button focusable
					if (el.id === 'mobile-menu-toggle') {
						return; // Skip the hamburger button
					}
					el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '0');
					el.setAttribute('tabindex', '-1');
				});
			}
		}

		// Find and hide any other focusable elements outside the menu drawer
		document.querySelectorAll('body > *').forEach((el) => {
			if (el.nodeType === 1) {
				// Element node
				const focusableChildren = el.querySelectorAll(
					'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
				);
				focusableChildren.forEach((focusable) => {
					// Only hide if NOT inside the mobile menu drawer or its wrapper
					if (!focusable.closest('#mobile-menu, .menu-mobile-wrapper')) {
						focusable.setAttribute(
							'data-menu-trap-tabindex',
							focusable.getAttribute('tabindex') || '0'
						);
						focusable.setAttribute('tabindex', '-1');
					}
				});
			}
		});
	};

	/**
	 * Restore background content to screen readers and keyboard navigation.
	 */
	const showBackgroundContent = () => {
		const mainContent = document.querySelector('main, #main, [role="main"]');
		const footer = document.querySelector('footer, [role="contentinfo"]');
		const navbar = document.getElementById('mobile-navbar');

		// Remove aria-hidden and inert from main content areas
		if (mainContent) {
			mainContent.removeAttribute('aria-hidden');
			mainContent.removeAttribute('inert');
		}
		if (footer) {
			footer.removeAttribute('aria-hidden');
			footer.removeAttribute('inert');
		}

		// Restore the menu bar to keyboard navigation
		if (navbar) {
			const menuBar = navbar.querySelector('.main-bar');
			if (menuBar) {
				// Restore original tabindex
				menuBar.querySelectorAll('button, a[href], a').forEach((el) => {
					const originalTabindex = el.getAttribute('data-original-tabindex');
					if (originalTabindex && originalTabindex !== '0') {
						el.setAttribute('tabindex', originalTabindex);
					} else {
						el.removeAttribute('tabindex');
					}
					el.removeAttribute('data-original-tabindex');
				});
			}
		}

		// Restore all other focusable elements
		document.querySelectorAll('[data-menu-trap-tabindex]').forEach((el) => {
			const originalTabindex = el.getAttribute('data-menu-trap-tabindex');
			if (originalTabindex && originalTabindex !== '0') {
				el.setAttribute('tabindex', originalTabindex);
			} else {
				el.removeAttribute('tabindex');
			}
			el.removeAttribute('data-menu-trap-tabindex');
		});
	};

	/**
	 * Ensure menu items are in tab order.
	 */
	const ensureMenuItemsTabable = () => {
		if (!mobileMenuDrawer) return;

		// Explicitly set tabindex="0" on all menu links and buttons
		// This ensures they're definitely in the tab order
		const allMenuLinks = mobileMenuDrawer.querySelectorAll('a[href], button');
		allMenuLinks.forEach((el) => {
			if (!el.hasAttribute('tabindex') || el.getAttribute('tabindex') === '-1') {
				el.setAttribute('tabindex', '0');
			}
		});
	};

	/**
	 * Handle mobile menu open/close state changes.
	 * Sets up focus trap when opened, restores focus when closed.
	 */
	const handleMenuToggle = () => {
		if (isMenuOpen()) {
			// Menu opened
			lastFocusedBeforeOpen = document.activeElement;

			// Hide background content from keyboard/screen readers
			hideBackgroundContent();

			// Ensure menu items are tabbable
			ensureMenuItemsTabable();

			// Get focusable elements after ensuring they're tabbable
			focusableElements = getFocusableElements();

			// Focus first element in menu
			if (focusableElements.length > 0) {
				focusableElements[0].focus();
			}

			// Set up event listeners
			document.addEventListener('keydown', trapFocus);
			document.addEventListener('keydown', handleEscape);
			document.addEventListener('keydown', handleArrowKeys);
		} else {
			// Menu closed
			document.removeEventListener('keydown', trapFocus);
			document.removeEventListener('keydown', handleEscape);
			document.removeEventListener('keydown', handleArrowKeys);

			// Restore background content to keyboard/screen readers
			showBackgroundContent();

			// Restore focus to element that opened the menu
			if (lastFocusedBeforeOpen && lastFocusedBeforeOpen.focus) {
				lastFocusedBeforeOpen.focus();
			}
			lastFocusedBeforeOpen = null;
		}
	};

	/**
	 * Initialize roving tabindex for mobile menu bar buttons.
	 */
	const initMenuBarButtons = () => {
		const navbar = document.getElementById('mobile-navbar');
		if (!navbar) return;

		const endButtons = navbar.querySelector('.main-bar .end');
		if (!endButtons) return;

		// Get all buttons and links in the end section (search, cart, hamburger)
		// Query for both buttons and links (including links without href during loading)
		const allElements = endButtons.querySelectorAll('button, a, [role="button"]');
		const buttons = Array.from(allElements).filter((el) => {
			// Only check if element is visible
			if (el.offsetParent === null) return false;

			// Check if it's inside the mobile menu drawer (exclude drawer items, keep only top bar)
			const isInDrawer = el.closest('#mobile-menu, .menu-mobile-wrapper');
			if (isInDrawer) return false;

			return true;
		});

		if (buttons.length === 0) return;

		/**
		 * Handle keyboard navigation between menu bar buttons.
		 * @param {KeyboardEvent} e
		 */
		const handleButtonKeydown = (e) => {
			const index = buttons.indexOf(e.target);
			if (index === -1) return;

			switch (e.key) {
				case 'ArrowLeft':
				case 'Left':
					e.preventDefault();
					const prevIndex = index > 0 ? index - 1 : buttons.length - 1;
					buttons[prevIndex].focus();
					break;

				case 'ArrowRight':
				case 'Right':
					e.preventDefault();
					const nextIndex = index < buttons.length - 1 ? index + 1 : 0;
					buttons[nextIndex].focus();
					break;

				case 'Home':
					e.preventDefault();
					buttons[0].focus();
					break;

				case 'End':
					e.preventDefault();
					buttons[buttons.length - 1].focus();
					break;
			}
		};

		// Attach keyboard handlers
		buttons.forEach((btn) => {
			btn.addEventListener('keydown', handleButtonKeydown);
		});
	};

	/**
	 * Observe changes to mobile menu toggle state.
	 * Uses MutationObserver to detect when 'toggle' class is added/removed.
	 */
	const observeMenuToggle = () => {
		if (!mobileMenuDrawer || !mobileMenuToggle) return;

		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.attributeName === 'class') {
					handleMenuToggle();
				}
			});
		});

		observer.observe(mobileMenuDrawer, {
			attributes: true,
			attributeFilter: ['class'],
		});

		// Also update aria-expanded on toggle button
		const toggleObserver = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.attributeName === 'class') {
					const isOpen = mobileMenuToggle.classList.contains('toggle');
					mobileMenuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
				}
			});
		});

		toggleObserver.observe(mobileMenuToggle, {
			attributes: true,
			attributeFilter: ['class'],
		});
	};

	/**
	 * Observe each submenu button and mirror its toggle state to the sibling menu-link.
	 * The jtoggle plugin only updates the button and submenu — this keeps the <a> in sync.
	 */
	const initSubmenuLinkSync = () => {
		if (!mobileMenuDrawer) return;

		mobileMenuDrawer.querySelectorAll('.submenu-button').forEach((button) => {
			const link = button.closest('.narrow-container')?.querySelector('a.menu-link');
			if (!link) return;

			new MutationObserver(() => {
				const open = button.classList.contains('toggle');
				link.classList.toggle('toggle', open);
			}).observe(button, { attributes: true, attributeFilter: ['class'] });
		});
	};

	/**
	 * Re-initialize keyboard on cart updates (WooCommerce AJAX cart fragments).
	 */
	const handleCartUpdate = () => {
		if (typeof jQuery !== 'undefined') {
			jQuery(document.body).on(
				'wc_fragments_refreshed wc_fragments_loaded updated_wc_div',
				() => {
					// Wait for DOM to update, then re-initialize
					setTimeout(() => {
						initMenuBarButtons();
					}, 100);
				}
			);
		}
	};

	/**
	 * Wait for elements to be available, then initialize.
	 * Cart may load asynchronously via WooCommerce fragments.
	 */
	const waitForElements = (callback, maxAttempts = 20, delay = 100) => {
		let attempts = 0;

		const check = () => {
			const navbar = document.getElementById('mobile-navbar');
			if (!navbar) {
				if (attempts < maxAttempts) {
					attempts++;
					setTimeout(check, delay);
				}
				return;
			}

			const endButtons = navbar.querySelector('.main-bar .end');
			if (!endButtons) {
				if (attempts < maxAttempts) {
					attempts++;
					setTimeout(check, delay);
				}
				return;
			}

			// Check if we have the key elements: search button, cart link, hamburger button
			const searchButton = endButtons.querySelector('button[id*="searchbar"]');
			const cartLink = endButtons.querySelector('a[id*="cart"], a.mobile-cart, a.cart');
			const hamburger = endButtons.querySelector('button[id*="menu-toggle"]');

			// Wait for all three elements or timeout
			if ((searchButton && cartLink && hamburger) || attempts >= maxAttempts) {
				callback();
			} else {
				attempts++;
				setTimeout(check, delay);
			}
		};

		check();
	};

	/**
	 * Initialize mobile navigation keyboard accessibility.
	 */
	const init = () => {
		const navbar = document.getElementById('mobile-navbar');
		if (!navbar) return;

		mobileMenuDrawer = navbar.querySelector('#mobile-menu');
		mobileMenuToggle = navbar.querySelector('#mobile-menu-toggle');

		if (!mobileMenuDrawer || !mobileMenuToggle) return;

		// Wait for all elements (including async cart) to be ready
		waitForElements(() => {
			// Initialize menu bar buttons with roving tabindex
			initMenuBarButtons();

			// Observe menu toggle state changes
			observeMenuToggle();

			// Sync submenu link toggle state with submenu button
			initSubmenuLinkSync();

			// Handle cart updates
			handleCartUpdate();
		});
	};

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
